import ManagerRepository from 'coreClasses/ManagerRepository';
import ProductItemPriceModel from './ProductItemPriceModel';

import {ProductItem} from 'coreStore/data/orders/types';
import AbstractProductModel from 'coreClasses/product/AbstractProductModel';
import CustomizedProductDecorator from 'coreClasses/product/customized/CustomizedProductDecorator';
import {ICustomizationConfiguration} from 'coreClasses/product/customized/ICustomizedProduct';

/**
 * Represents an order product item
 */
abstract class ProductItemModel {
	private managerRepository: ManagerRepository;
	private productItem: ProductItem;
	private currency: string;
	private customizationConfiguration: ICustomizationConfiguration;
	private bundleComponents: ProductItemModel[] = []; // store bundle components here
	private price: number;
	private priceAfterItemDiscount: number;

	/**
	 * @constructor
	 * @param {ManagerRepository} managerRepository - ManagerRepository instance
	 * @param {ProductItem} productItem - the product item
	 * @param {string} currency - the currency
	 */
	constructor(managerRepository: ManagerRepository, productItem: ProductItem, currency: string) {
		this.managerRepository = managerRepository;
		this.productItem = productItem;
		this.currency = currency;

		Object.assign(this, productItem);
	}

	/**
	 * Returns price model for the product line item.
	 */
	get priceModel() {
		return new ProductItemPriceModel(this);
	}

	/**
	 * Returns container currency
	 */
	getCurrency() {
		return this.currency;
	}

	/**
	 * Returns the price model
	 */
	getPriceModel() {
		return this.priceModel;
	}

	/**
	 * Returns the product item id
	 * @returns {string} item id
	 */
	getItemId(): string {
		return this.productItem.itemId;
	}

	/**
	 * Returns the product item itemText
	 * @returns {string} item itemText
	 */
	getItemText(): string {
		return this.productItem.itemText;
	}

	/**
	 * Returns the product item price
	 * @returns {number} item price
	 */
	getPrice(): number {
		if (!this.price) {
			let price = this.productItem.price;

			if (this.isParentBundleKit() && this.bundleComponents.length > 0) {
				price = this.calculateTotalBundlePrice();
			}

			this.price = price;
		}

		return this.price;
	}

	/**
	 * Returns the product item price after item discount
	 * @returns {number} item price after item discount
	 */
	getPriceAfterItemDiscount(): number {
		if (!this.priceAfterItemDiscount) {
			let priceAfterItemDiscount = this.productItem.priceAfterItemDiscount;

			if (this.isParentBundleKit() && this.bundleComponents.length > 0) {
				priceAfterItemDiscount = this.calculateTotalBundlePriceAfterDiscount();
			}

			this.priceAfterItemDiscount = priceAfterItemDiscount;
		}

		return this.priceAfterItemDiscount;
	}

	/**
	 * Returns the product item quantity
	 * @returns {number} item quantity
	 */
	getQuantity(): number {
		return this.productItem.quantity;
	}

	/**
	 * Returns the product item product id
	 * @returns {string} product id
	 */
	getProductId(): string {
		return this.productItem.productId;
	}

	/**
	 * Returns the product item variation group id
	 * @returns {string} variation group id
	 */
	getVariationGroupId(): string {
		return this.productItem.c_variationGroupId;
	}

	/**
	 * Checks if product line item is returnable
	 * @returns {boolean} - is returnable
	 */
	isReturnable(): boolean {
		return (this.productItem as ProductItem & {c_isReturnable?: boolean}).c_isReturnable;
	}

	/**
	 * Return quantity of returned items
	 * @returns {number} - quantity of returned items
	 */
	getReturnedQuantity(): number {
		return this.productItem?.c_returnedQuantity ?? 0;
	}

	/**
	 * Checks if product line item is exchanged
	 * @returns {boolean} - is exchanged
	 */
	isExchanged(): boolean {
		return !!this.productItem.c_originalExchangedPrice;
	}

	/**
	 * Returns original price of exchanged item
	 * @returns {number} - original price
	 */
	getOriginalExchangedPrice(): number {
		return this.productItem.c_originalExchangedPrice;
	}

	/**
	 * Returns product model for product line item
	 * @returns {AbstractProductModel|CustomizedProductDecorator} product model
	 */
	getProductModel(): AbstractProductModel | typeof CustomizedProductDecorator {
		const productModel = this.managerRepository
			.getProductsMgr()
			.getProduct(this.getVariationGroupId() || this.getProductId()) as any;

		if (productModel?.isReady()) {
			if (this.isCustomizable()) {
				const CustomizedProductModel = CustomizedProductDecorator(productModel.constructor);

				(this as any).productModel = new CustomizedProductModel(
					this.managerRepository,
					productModel.getProductObject(),
					this.getCustomizationConfiguration(),
				);
			} else {
				(this as any).productModel = productModel;
			}
		}

		return (this as any).productModel || productModel;
	}

	/**
	 * Checks if the product line item is customizable
	 * @returns {boolean} - true if the product line item is customizable
	 */
	isCustomizable(): boolean {
		return !!this.productItem?.c_customProducts;
	}

	/**
	 * Checks if the product line item is Part of bundle Kit
	 * @returns {boolean} - true if the product line item is part of bundle kit
	 */
	isPartOfBundleKit(): boolean {
		return !!this.productItem?.c_isPartOfBundleKit;
	}

	/**
	 * Checks if the product is a bundle kit main product
	 * @returns {boolean} - true if the product line item is a bundle kit product
	 */
	isParentBundleKit(): boolean {
		return !!this.productItem?.c_isParentBundleKit;
	}

	/**
	 * Determines if the product line item is a component of a bundle kit,
	 * but not the main bundle kit product.
	 * @returns {boolean} - true if the product line item is a component of a bundle kit, false otherwise
	 */
	isComponentBundleKit(): boolean {
		return this.isPartOfBundleKit() && !this.isParentBundleKit();
	}

	/**
	 * Checks if the product line item is a monogram service item
	 * @returns {boolean} - true if the product line item is a monogram service item
	 */
	isMonogramServiceItem(): boolean {
		return !!this.productItem?.c_isMonogramServiceItem;
	}

	/**
	 * Determines if the product item is visible.
	 * A product item is considered visible if it is not a component of a bundle kit.
	 *
	 * @returns {boolean} - True if the product item is visible, false if it is a bundle kit component.
	 */
	isVisible(): boolean {
		return !this.isComponentBundleKit();
	}

	/**
	 * Returns id of bundle kit main product
	 *
	 * @returns {string} Bundle kit main product id, or an empty string if not available.
	 */
	getParentBundleKitId(): string {
		return this.productItem?.c_bundleKitId || '';
	}

	/**
	 * Adds a bundle component to this main product item.
	 *
	 * @param {ProductItemModel} bundleComponent - The bundle component to add.
	 * @returns {void}
	 */
	addBundleComponent(bundleComponent: ProductItemModel): void {
		this.bundleComponents.push(bundleComponent);
	}

	/**
	 * Calculates the total price of this bundle kit, including the prices of all associated bundle components.
	 *
	 * @returns {number} - The total price of the bundle kit, including all components.
	 */
	calculateTotalBundlePrice(): number {
		let totalPrice = this.productItem.price;

		this.bundleComponents.forEach((component) => {
			totalPrice += component.getPrice();
		});

		return totalPrice;
	}

	/**
	 * Calculates the total discounted price of this bundle kit, including the discounted prices of all associated bundle components.
	 *
	 * @returns {number} - The total discounted price of the bundle kit, including all components.
	 */
	calculateTotalBundlePriceAfterDiscount(): number {
		let totalDiscountedPrice = this.productItem.priceAfterItemDiscount;

		this.bundleComponents.forEach((component) => {
			totalDiscountedPrice += component.getPriceAfterItemDiscount();
		});

		return totalDiscountedPrice;
	}

	/**
	 * Returns customization configuration
	 * @returns {ICustomizationConfiguration} - customization configuration
	 */
	getCustomizationConfiguration(): ICustomizationConfiguration {
		if (this.customizationConfiguration === undefined) {
			if (this.isCustomizable()) {
				try {
					const parsedConfiguration: ICustomizationConfiguration = JSON.parse(
						this.productItem.c_customProducts,
					);

					this.customizationConfiguration = parsedConfiguration;
					this.customizationConfiguration.url = {
						queryParams: {
							customizationPliID: this.getItemId(),
						},
					};
				} catch {
					this.customizationConfiguration = null;
				}
			} else {
				this.customizationConfiguration = null;
			}
		}

		return this.customizationConfiguration;
	}

	/**
	 * Returns return status, if present
	 * @returns {string | undefined} - status of the returned item
	 */
	getReturnStatus(): string | undefined {
		return this.productItem?.c_returnStatus;
	}

	/**
	 * Checks if the product line item is bonus
	 * @returns {boolean} - true if the product line item is bonus
	 */
	isBonus(): boolean {
		return !!this.productItem?.bonusProductLineItem;
	}

	/**
	 * Returns engraving message
	 * @returns {string} engraving message
	 */
	getEngraveMessage(): string {
		return this.productItem?.c_engraveMessage || '';
	}

	/**
	 * Checks if the product line item is personalized
	 * @returns {boolean} - true if the product line item is personalized
	 */
	isPersonalized(): boolean {
		return this.productItem.c_isPersonalized || false;
	}
	/**
	 * Returns personalization message
	 * @returns {string} engraving message
	 */
	getPersonalizationMessage(): string {
		return this.productItem?.c_personalizationMessage || '';
	}

	/**
	 * Returns position personalization message
	 * @returns {string} position personalization message
	 */
	getPositionPersonalizationMessage(): string {
		return this.productItem?.c_positionPersonalizationMessage || '';
	}

	/**
	 * Returns personalization number
	 * @returns {string} personalization number
	 */
	getPersonalizationNumber(): string {
		return this.productItem?.c_personalizationNumber || '';
	}

	/**
	 * Returns position personalization number
	 * @returns {string} position personalization number
	 */
	getPositionPersonalizationNumber(): string {
		return this.productItem?.c_positionPersonalizationNumber || '';
	}

	/**
	 * Returns the size if it's present in item
	 */
	getSize(): string {
		return this.productItem?.c_size;
	}

	/**
	 * Returns the product item price after order discount
	 * @returns {number} order price after order discount
	 */
	getPriceAfterOrderDiscount(): number {
		return this.productItem.priceAfterOrderDiscount;
	}

	/**
	 * Returns the prorated price
	 * @returns {number} prorated price
	 */
	getProratedPrice(): number {
		return this.productItem.proratedPrice;
	}

	/**
	 * Returns the product prorated price adjustment
	 * @returns {Number} prorated price adjustment
	 */
	getProratedPriceAdjustmentPrices(): number {
		return this.productItem.proratedPriceAdjustmentPrices || this.productItem.c_proratedPriceAdjustmentPrices;
	}
}

export default ProductItemModel;
